import type { KeyboardEvent } from "react";

interface TabListProps<T extends string> {
  /** Вкладки в порядке показа. */
  tabs: readonly T[];
  labels: Readonly<Record<T, string>>;
  active: T;
  onSelect: (tab: T) => void;
  /** Доступное имя группы вкладок для скринридера. */
  label: string;
  /** Префикс идентификаторов вкладок: `<idPrefix>-<tab>`; по ним панель ссылается на свою вкладку. */
  idPrefix: string;
  /** Идентификатор панели, которой управляют вкладки. */
  panelId: string;
}

/**
 * Список вкладок раздела по шаблону WAI-ARIA Tabs.
 *
 * Отвечает за разметку `tablist`/`tab` и клавиатуру: стрелки влево и вправо
 * переключают вкладку по кругу и переносят на неё фокус, а в порядке Tab
 * участвует только активная вкладка. Сама панель остаётся на стороне
 * страницы: она должна иметь `id={panelId}`, `role="tabpanel"` и
 * `aria-labelledby={`${idPrefix}-${active}`}`.
 *
 * Используется на страницах с несколькими разделами в одном экране
 * (контрагенты, почтовые ящики) вместо копий одной и той же разметки.
 */
export function TabList<T extends string>({
  tabs,
  labels,
  active,
  onSelect,
  label,
  idPrefix,
  panelId,
}: TabListProps<T>) {
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const delta =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next =
      tabs[(tabs.indexOf(active) + delta + tabs.length) % tabs.length];
    if (next !== undefined) {
      onSelect(next);
      document.getElementById(`${idPrefix}-${next}`)?.focus();
    }
  }

  return (
    <div className="cp-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab}
          id={`${idPrefix}-${tab}`}
          type="button"
          role="tab"
          aria-selected={active === tab}
          aria-controls={panelId}
          tabIndex={active === tab ? 0 : -1}
          className="cp-tab"
          onClick={() => onSelect(tab)}
          onKeyDown={onKeyDown}
        >
          {labels[tab]}
        </button>
      ))}
    </div>
  );
}
